import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Card,
  SimpleGrid,
  HStack,
  Text,
  Badge,
  Button,
  Icon,
  Spinner,
  Alert,
  Dialog,
  Input,
  Field,
  Select,
  createListCollection,
  Table,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useColorModeValue } from '../hooks/useColorModeValue';
import {
  FiShield,
  FiEye,
  FiDownload,
  FiTrash2,
  FiSettings,
  FiAlertCircle,
  FiClock,
  FiUser,
  FiMapPin,
  FiLogOut,
  FiDatabase,
} from 'react-icons/fi';
import {
  useComplianceDashboard,
  useAuditLogs,
  useSecurityIncidents,
  useAccessControlStatus,
  useSecurityStatus,
  useExportAuditLogs,
  useExportUserData,
  useDeleteUserData,
  useCleanupExpiredData,
  useRunSecurityAssessment,
} from '../hooks/useApi';
import { toaster } from './ui/toaster';

const SecurityComplianceSection: React.FC = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // State for modals
  const [isOpen, setIsOpen] = useState(false);
  const [isAccessLogsOpen, setIsAccessLogsOpen] = useState(false);
  const [isAuditLogsOpen, setIsAuditLogsOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'export' | 'delete'>('export');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const onAccessLogsOpen = () => setIsAccessLogsOpen(true);
  const onAuditLogsOpen = () => setIsAuditLogsOpen(true);

  // Audit log modal: search / filter / pagination state
  const AUDIT_PAGE_SIZE = 25;
  const [auditSearchInput, setAuditSearchInput] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditResourceFilter, setAuditResourceFilter] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState<'' | 'success' | 'failed'>('');
  const [auditPage, setAuditPage] = useState(0);

  // Debounce the search input
  useEffect(() => {
    const t = setTimeout(() => setAuditSearch(auditSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [auditSearchInput]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setAuditPage(0);
  }, [auditSearch, auditActionFilter, auditResourceFilter, auditStatusFilter]);

  // API hooks
  const { data: complianceData, isLoading: complianceLoading, error: complianceError } = useComplianceDashboard();
  const { data: auditLogs, isLoading: auditLoading } = useAuditLogs({ limit: 10 });
  const { data: modalAuditLogs, isLoading: modalAuditLoading } = useAuditLogs({
    search: auditSearch || undefined,
    action: auditActionFilter || undefined,
    resource_type: auditResourceFilter || undefined,
    success: auditStatusFilter === '' ? undefined : auditStatusFilter === 'success',
    limit: AUDIT_PAGE_SIZE,
    offset: auditPage * AUDIT_PAGE_SIZE,
  });
  const { data: securityIncidents } = useSecurityIncidents();
  const { data: accessControl } = useAccessControlStatus();
  const { data: securityStatus } = useSecurityStatus();
  
  // Mutations
  const exportAuditLogs = useExportAuditLogs();
  const exportUserData = useExportUserData();
  const deleteUserData = useDeleteUserData();
  const cleanupExpiredData = useCleanupExpiredData();
  const runSecurityAssessment = useRunSecurityAssessment();

  const handleExportAuditLogs = async () => {
    try {
      const blob = await exportAuditLogs.mutateAsync({
        format: 'json',
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Last 30 days
        end_date: new Date().toISOString(),
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('admin.security.compliance.toasts.auditLogsExported'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.security.compliance.toasts.auditLogsExportFailed'),
        type: 'error',
      });
    }
  };

  const handleExportUserData = async (userId: number) => {
    try {
      const blob = await exportUserData.mutateAsync({
        user_id: userId,
        format: 'json',
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_data_${userId}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('admin.security.compliance.toasts.userDataExported'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.security.compliance.toasts.userDataExportFailed'),
        type: 'error',
      });
    }
  };

  const handleDeleteUserData = async (userId: number) => {
    try {
      await deleteUserData.mutateAsync({
        user_id: userId,
        reason: 'right_to_be_forgotten',
      });
      
      toaster.create({
        title: t('admin.security.compliance.toasts.userDataDeleted'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.security.compliance.toasts.userDataDeleteFailed'),
        type: 'error',
      });
    }
  };

  const handleCleanupExpiredData = async () => {
    try {
      await cleanupExpiredData.mutateAsync({});
      
      toaster.create({
        title: t('admin.security.compliance.toasts.expiredDataCleaned'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.security.compliance.toasts.expiredDataCleanupFailed'),
        type: 'error',
      });
    }
  };

  const handleRunSecurityAssessment = async () => {
    try {
      await runSecurityAssessment.mutateAsync();
      
      toaster.create({
        title: t('admin.security.compliance.toasts.securityAssessmentCompleted'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.security.compliance.toasts.securityAssessmentFailed'),
        type: 'error',
      });
    }
  };

  const complianceLabel = (score?: number) =>
    (score || 0) >= 80
      ? t('admin.security.compliance.status.compliant')
      : t('admin.security.compliance.status.needsAttention');

  const securityStatusLabel = (status?: string) => {
    if (!status) return t('admin.security.compliance.status.unknown');
    const normalized = status.toLowerCase().replace(/\s+/g, '');
    if (normalized === 'active') return t('admin.security.compliance.status.active');
    if (normalized === 'valid') return t('admin.security.compliance.status.valid');
    return status;
  };

  const auditActionLabel = (action: string) => {
    if (action.includes('login_success')) return t('admin.security.compliance.auditActions.successfulLogin');
    if (action.includes('login_failed')) return t('admin.security.compliance.auditActions.failedLogin');
    if (action.includes('logout')) return t('admin.security.compliance.auditActions.userLogout');
    if (action.includes('data_portability')) return t('admin.security.compliance.auditActions.dataExport');
    if (action.includes('data_deletion')) return t('admin.security.compliance.auditActions.dataDeletion');
    return action;
  };

  const severityLabel = (severity: string) => {
    if (severity === 'critical') return t('admin.security.compliance.severity.critical');
    if (severity === 'high') return t('admin.security.compliance.severity.high');
    if (severity === 'medium') return t('admin.security.compliance.severity.medium');
    return severity;
  };

  if (complianceLoading || auditLoading) {
    return (
      <Box p={6}>
        <VStack gap={6} align="center" justify="center" h="400px">
          <Spinner size="xl" color="blue.500" />
          <Text>{t('admin.security.compliance.loading')}</Text>
        </VStack>
      </Box>
    );
  }

  if (complianceError) {
    return (
      <Box p={6}>
        <Alert.Root status="error" borderRadius="xl">
          <Alert.Indicator />
          <VStack align="start" gap={1}>
            <Text fontWeight="medium">{t('admin.security.compliance.errors.loadComplianceData')}</Text>
            <Text fontSize="sm">{t('admin.security.compliance.errors.refreshOrTryLater')}</Text>
          </VStack>
        </Alert.Root>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Heading size="lg">{t('admin.security.compliance.title')}</Heading>
        
        {/* Compliance Overview */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.security.compliance.sections.complianceOverview')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <VStack gap={2} p={4} bg="green.50" borderRadius="md">
                <Icon as={FiShield} color="green.500" boxSize={6} />
                <Text fontSize="sm" fontWeight="semibold">GDPR</Text>
                <Badge colorScheme="green">
                  {complianceLabel(complianceData?.compliance_overview?.gdpr_score)}
                </Badge>
                <Text fontSize="xs" color="gray.600">
                  {t('admin.security.compliance.labels.score', { score: complianceData?.compliance_overview?.gdpr_score || 95 })}
                </Text>
              </VStack>
              <VStack gap={2} p={4} bg="blue.50" borderRadius="md">
                <Icon as={FiShield} color="blue.500" boxSize={6} />
                <Text fontSize="sm" fontWeight="semibold">SOC 2</Text>
                <Badge colorScheme="blue">
                  {complianceLabel(complianceData?.compliance_overview?.soc2_score)}
                </Badge>
                <Text fontSize="xs" color="gray.600">
                  {t('admin.security.compliance.labels.score', { score: complianceData?.compliance_overview?.soc2_score || 88 })}
                </Text>
              </VStack>
              <VStack gap={2} p={4} bg="purple.50" borderRadius="md">
                <Icon as={FiShield} color="purple.500" boxSize={6} />
                <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.overall')}</Text>
                <Badge colorScheme="purple">
                  {complianceLabel(complianceData?.compliance_overview?.overall_score)}
                </Badge>
                <Text fontSize="xs" color="gray.600">
                  {t('admin.security.compliance.labels.score', { score: complianceData?.compliance_overview?.overall_score || 91 })}
                </Text>
              </VStack>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>

        {/* Security Status & Access Control */}
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="md">{t('admin.security.compliance.sections.securityStatus')}</Heading>
            </Card.Header>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.encryptionStatus')}</Text>
                  <Badge colorScheme={securityStatus?.encryption?.status === 'Active' ? 'green' : 'orange'}>
                    {securityStatusLabel(securityStatus?.encryption?.status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.sslCertificate')}</Text>
                  <Badge colorScheme={securityStatus?.ssl_certificate?.status === 'Valid' ? 'green' : 'blue'}>
                    {securityStatusLabel(securityStatus?.ssl_certificate?.status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.lastSecurityScan')}</Text>
                  <Badge colorScheme={securityStatus?.last_security_scan?.status?.includes('ago') ? 'green' : 'gray'}>
                    {securityStatus?.last_security_scan?.status || t('admin.security.compliance.status.unknown')}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.securityScore')}</Text>
                  <Badge colorScheme={securityStatus?.security_score >= 80 ? 'green' : 'orange'}>
                    {securityStatus?.security_score || 0}%
                  </Badge>
                </HStack>
                <Button 
                  colorScheme="blue" 
                  size="sm"
                  onClick={handleRunSecurityAssessment}
                  loading={runSecurityAssessment.isLoading}
                >
                  <FiShield /> {t('admin.security.compliance.buttons.runSecurityAssessment')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="md">{t('admin.security.compliance.sections.accessControlMonitoring')}</Heading>
            </Card.Header>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.last24hLogins')}</Text>
                  <Badge colorScheme="blue">{accessControl?.last_24_hours?.total_logins || 0}</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.failedAttempts')}</Text>
                  <Badge colorScheme="orange">{accessControl?.last_24_hours?.failed_logins || 0}</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.successRate')}</Text>
                  <Badge colorScheme="green">{accessControl?.last_24_hours?.success_rate || 0}%</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text>{t('admin.security.compliance.labels.suspiciousActivity')}</Text>
                  <Badge colorScheme="green">0</Badge>
                </HStack>
                <Button 
                  colorScheme="purple" 
                  size="sm"
                  onClick={onAccessLogsOpen}
                >
                  <FiEye /> {t('admin.security.compliance.buttons.viewAccessLogs')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Audit Logs */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <HStack justify="space-between">
              <Heading size="md">{t('admin.security.compliance.sections.auditLogs')}</Heading>
              <HStack gap={2}>
                <Button 
                  colorScheme="green" 
                  size="sm"
                  onClick={handleExportAuditLogs}
                  loading={exportAuditLogs.isLoading}
                >
                  <FiDownload /> {t('admin.security.compliance.buttons.exportLogs')}
                </Button>
                <Button colorScheme="blue" size="sm" onClick={onAuditLogsOpen}>
                  <FiEye /> {t('admin.security.compliance.buttons.viewAll')}
                </Button>
              </HStack>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              {/* Summary Stats */}
              <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                <VStack gap={1} p={2} bg="blue.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="semibold" color="blue.700">{t('admin.security.compliance.labels.totalEvents')}</Text>
                  <Text fontSize="lg" fontWeight="bold" color="blue.600">
                    {auditLogs?.total || 0}
                  </Text>
                </VStack>
                <VStack gap={1} p={2} bg="green.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="semibold" color="green.700">{t('admin.security.compliance.labels.successRate')}</Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.600">
                    {auditLogs?.logs && auditLogs.logs.length > 0 
                      ? Math.round((auditLogs.logs.filter((log: any) => log.success).length / auditLogs.logs.length) * 100)
                      : 0}%
                  </Text>
                </VStack>
                <VStack gap={1} p={2} bg="orange.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="semibold" color="orange.700">{t('admin.security.compliance.status.failed')}</Text>
                  <Text fontSize="lg" fontWeight="bold" color="orange.600">
                    {auditLogs?.logs?.filter((log: any) => !log.success).length || 0}
                  </Text>
                </VStack>
              </SimpleGrid>

              {/* Recent Activity */}
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">{t('admin.security.compliance.labels.recentActivity')}</Text>
                <Badge colorScheme="blue">{t('admin.security.compliance.labels.totalEventsCount', { count: auditLogs?.total || 0 })}</Badge>
              </HStack>
              <Box maxH="200px" overflowY="auto">
                {auditLogs?.logs && auditLogs.logs.length > 0 ? (
                  <VStack gap={2} align="stretch">
                    {auditLogs.logs.slice(0, 5).map((log: any) => (
                      <HStack key={log.id} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                        <VStack align="start" gap={1}>
                          <HStack gap={2}>
                            <Icon 
                              as={log.action.includes('login') ? FiUser : 
                                  log.action.includes('logout') ? FiLogOut :
                                  log.action.includes('data') ? FiDatabase :
                                  FiEye} 
                              color={log.success ? 'green.500' : 'red.500'} 
                              boxSize={3} 
                            />
                            <Text fontSize="sm" fontWeight="medium">
                              {log.action.replace('user_', '').replace('_', ' ').toUpperCase()}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {log.username || log.context_data?.email || t('admin.security.compliance.status.unknown')} • {log.ip_address}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {new Date(log.created_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })} • {log.context_data?.login_method || t('admin.security.compliance.status.unknownMethod')}
                          </Text>
                        </VStack>
                        <VStack align="end" gap={1}>
                          <Badge colorScheme={log.success ? 'green' : 'red'} size="sm">
                            {log.success ? t('admin.security.compliance.status.success') : t('admin.security.compliance.status.failed')}
                          </Badge>
                          {log.error_message && (
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Icon as={FiAlertCircle} color="red.400" boxSize={3} />
                              </Tooltip.Trigger>
                              <Tooltip.Content>{log.error_message}</Tooltip.Content>
                            </Tooltip.Root>
                          )}
                        </VStack>
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {t('admin.security.compliance.empty.noRecentAuditLogs')}
                  </Text>
                )}
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* GDPR Tools */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.security.compliance.sections.gdprTools')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium">{t('admin.security.compliance.labels.dataPortability')}</Text>
                <Button 
                  colorScheme="blue" 
                  size="sm"
                  onClick={() => {
                    setSelectedAction('export');
                    onOpen();
                  }}
                >
                  <FiDownload /> {t('admin.security.compliance.buttons.exportUserData')}
                </Button>
                <Text fontSize="xs" color="gray.500">{t('admin.security.compliance.descriptions.exportUserData')}</Text>
              </VStack>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium">{t('admin.security.compliance.labels.rightToBeForgotten')}</Text>
                <Button 
                  colorScheme="red" 
                  size="sm"
                  onClick={() => {
                    setSelectedAction('delete');
                    onOpen();
                  }}
                >
                  <FiTrash2 /> {t('admin.security.compliance.buttons.deleteUserData')}
                </Button>
                <Text fontSize="xs" color="gray.500">{t('admin.security.compliance.descriptions.deleteUserData')}</Text>
              </VStack>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium">{t('admin.security.compliance.labels.dataRetention')}</Text>
                <Button colorScheme="purple" size="sm">
                  <FiSettings /> {t('admin.security.compliance.buttons.retentionReport')}
                </Button>
                <Text fontSize="xs" color="gray.500">{t('admin.security.compliance.descriptions.retentionReport')}</Text>
              </VStack>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium">{t('admin.security.compliance.labels.dataCleanup')}</Text>
                <Button 
                  colorScheme="orange" 
                  size="sm"
                  onClick={handleCleanupExpiredData}
                  loading={cleanupExpiredData.isLoading}
                >
                  <FiTrash2 /> {t('admin.security.compliance.buttons.cleanupExpiredData')}
                </Button>
                <Text fontSize="xs" color="gray.500">{t('admin.security.compliance.descriptions.cleanupExpiredData')}</Text>
              </VStack>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>

        {/* Security Incidents */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.security.compliance.sections.securityIncidents')}</Heading>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">{t('admin.security.compliance.labels.last30Days')}</Text>
                <HStack gap={2}>
                  <Badge colorScheme="green">
                    {t('admin.security.compliance.severity.countCritical', { count: securityIncidents?.incidents?.filter((i: any) => i.severity === 'critical').length || 0 })}
                  </Badge>
                  <Badge colorScheme="orange">
                    {t('admin.security.compliance.severity.countHigh', { count: securityIncidents?.incidents?.filter((i: any) => i.severity === 'high').length || 0 })}
                  </Badge>
                  <Badge colorScheme="blue">
                    {t('admin.security.compliance.severity.countMedium', { count: securityIncidents?.incidents?.filter((i: any) => i.severity === 'medium').length || 0 })}
                  </Badge>
                </HStack>
              </HStack>
              <Box maxH="150px" overflowY="auto">
                {securityIncidents?.incidents && securityIncidents.incidents.length > 0 ? (
                  <VStack gap={2} align="stretch">
                    {securityIncidents.incidents.slice(0, 3).map((incident: any, index: number) => (
                      <HStack key={index} justify="space-between" p={2} bg={`${incident.severity === 'critical' ? 'red' : incident.severity === 'high' ? 'orange' : 'blue'}.50`} borderRadius="md">
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" fontWeight="medium">{incident.type}</Text>
                          <Text fontSize="xs" color="gray.500">{incident.description}</Text>
                        </VStack>
                        <Badge colorScheme={incident.severity === 'critical' ? 'red' : incident.severity === 'high' ? 'orange' : 'blue'} size="sm">
                          {severityLabel(incident.severity)}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {t('admin.security.compliance.empty.noSecurityIncidents')}
                  </Text>
                )}
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* GDPR/Data Deletion Modal */}
        <Dialog.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>
                  {selectedAction === 'export' ? t('admin.security.compliance.buttons.exportUserData') : t('admin.security.compliance.buttons.deleteUserData')}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4}>
                  <Field.Root>
                    <Field.Label>{t('admin.security.compliance.fields.userId')}</Field.Label>
                    <Input
                      type="number"
                      placeholder={t('admin.security.compliance.placeholders.enterUserId')}
                      value={selectedUserId || ''}
                      onChange={(e) => setSelectedUserId(parseInt(e.target.value) || null)}
                    />
                  </Field.Root>
                  {selectedAction === 'export' && (
                    <Field.Root>
                      <Field.Label>{t('admin.security.compliance.fields.exportFormat')}</Field.Label>
                      <Select.Root collection={createListCollection({ items: [{ label: t('admin.security.compliance.formats.json'), value: 'json' }, { label: t('admin.security.compliance.formats.zip'), value: 'zip' }] })}>
                        <Select.Trigger>
                          <Select.ValueText placeholder={t('admin.security.compliance.placeholders.selectFormat')} />
                        </Select.Trigger>
                        <Select.Content>
                          <Select.Item item={{ label: t('admin.security.compliance.formats.json'), value: 'json' }}>{t('admin.security.compliance.formats.json')}</Select.Item>
                          <Select.Item item={{ label: t('admin.security.compliance.formats.zip'), value: 'zip' }}>{t('admin.security.compliance.formats.zip')}</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.CloseTrigger asChild>
                  <Button variant="ghost">{t('admin.security.compliance.buttons.cancel')}</Button>
                </Dialog.CloseTrigger>
                <Button
                  colorScheme={selectedAction === 'export' ? 'blue' : 'red'}
                  onClick={() => {
                    if (selectedUserId) {
                      if (selectedAction === 'export') {
                        handleExportUserData(selectedUserId);
                      } else {
                        handleDeleteUserData(selectedUserId);
                      }
                      onClose();
                      setSelectedUserId(null);
                    }
                  }}
                  loading={selectedAction === 'export' ? exportUserData.isLoading : deleteUserData.isLoading}
                >
                  {selectedAction === 'export' ? t('admin.security.compliance.buttons.export') : t('admin.security.compliance.buttons.delete')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>

        {/* Access Logs Modal */}
        <Dialog.Root open={isAccessLogsOpen} onOpenChange={(e) => setIsAccessLogsOpen(e.open)} size="full">
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="90vw" maxH="85vh">
              <Dialog.Header>
                <Dialog.Title>{t('admin.security.compliance.dialogs.accessControlLogs')}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  {/* Summary Stats */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                    <VStack gap={2} p={3} bg="blue.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.totalLogins24h')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {accessControl?.last_24_hours?.total_logins || 0}
                      </Text>
                    </VStack>
                    <VStack gap={2} p={3} bg="green.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.successRate')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {accessControl?.last_24_hours?.success_rate || 0}%
                      </Text>
                    </VStack>
                    <VStack gap={2} p={3} bg="orange.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.failedAttempts')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">
                        {accessControl?.last_24_hours?.failed_logins || 0}
                      </Text>
                    </VStack>
                  </SimpleGrid>

                  {/* Security Alerts */}
                  {accessControl?.security_alerts?.suspicious_ips && accessControl.security_alerts.suspicious_ips.length > 0 && (
                    <Alert.Root status="warning" borderRadius="md">
                      <Alert.Indicator />
                      <VStack align="start" gap={1}>
                        <Text fontWeight="medium">{t('admin.security.compliance.alerts.suspiciousActivityDetected')}</Text>
                        <Text fontSize="sm">
                          {t('admin.security.compliance.alerts.suspiciousIps', { ips: accessControl.security_alerts.suspicious_ips.join(', ') })}
                        </Text>
                      </VStack>
                    </Alert.Root>
                  )}

                  {/* Recent Events Table */}
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">{t('admin.security.compliance.labels.recentAccessEvents')}</Text>
                  <Box maxH="400px" overflowY="auto" overflowX="auto">
                    {accessControl?.recent_events && accessControl.recent_events.length > 0 ? (
                      <Table.Root variant="line" size="sm" minW="1000px">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader fontSize="xs" w="12%">{t('admin.security.compliance.table.action')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="20%">{t('admin.security.compliance.table.user')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="15%">{t('admin.security.compliance.table.ipAddress')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="8%">{t('admin.security.compliance.table.status')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="12%">{t('admin.security.compliance.table.resource')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="10%">{t('admin.security.compliance.table.time')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="10%">{t('admin.security.compliance.table.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="13%">{t('admin.security.compliance.table.details')}</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {accessControl.recent_events.map((event: any) => (
                            <Table.Row key={event.id} _hover={{ bg: 'gray.50' }}>
                              <Table.Cell>
                                <HStack gap={2}>
                                  <Icon 
                                    as={event.action.includes('login') ? FiUser : 
                                        event.action.includes('logout') ? FiLogOut :
                                        event.action.includes('data') ? FiDatabase :
                                        FiEye} 
                                    color={event.action.includes('success') ? 'green.500' : 
                                           event.action.includes('failed') ? 'red.500' : 'blue.500'} 
                                    boxSize={4} 
                                  />
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {event.action.replace('user_', '').replace('_', ' ').toUpperCase()}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {auditActionLabel(event.action)}
                                    </Text>
                                  </VStack>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                    {event.username || (event.user_id ? t('admin.security.compliance.labels.userWithId', { id: event.user_id }) : t('admin.security.compliance.status.unknown'))}
                                  </Text>
                                  {event.context_data?.email && event.context_data.email !== event.username && (
                                    <Text fontSize="xs" color="gray.500">
                                      {event.context_data.email}
                                    </Text>
                                  )}
                                  {event.context_data?.login_method && (
                                    <Badge size="xs" colorScheme="blue" variant="subtle">
                                      {event.context_data.login_method}
                                    </Badge>
                                  )}
                                </VStack>
                              </Table.Cell>
                              <Table.Cell>
                                <HStack gap={1}>
                                  <Icon as={FiMapPin} color="gray.400" boxSize={3} />
                                  <Text fontSize="sm" fontFamily="mono" color="gray.700">
                                    {event.ip_address || t('admin.security.compliance.status.notAvailable')}
                                  </Text>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <Badge 
                                  colorScheme={event.success ? 'green' : 'red'} 
                                  size="sm"
                                  variant="subtle"
                                >
                                  {event.success ? t('admin.security.compliance.status.success') : t('admin.security.compliance.status.failed')}
                                </Badge>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {event.context_data?.resource_type || event.resource_type || t('admin.security.compliance.resources.authentication')}
                                  </Text>
                                  {event.context_data?.gdpr_action && (
                                    <Badge size="xs" colorScheme="purple" variant="subtle">
                                      {event.context_data.gdpr_action}
                                    </Badge>
                                  )}
                                </VStack>
                              </Table.Cell>
                              <Table.Cell>
                                <HStack gap={1}>
                                  <Icon as={FiClock} color="gray.400" boxSize={3} />
                                  <Text fontSize="sm" fontFamily="mono">
                                    {new Date(event.created_at).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    })}
                                  </Text>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm">
                                  {new Date(event.created_at).toLocaleDateString()}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  {event.context_data?.user_agent && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                          {event.context_data.user_agent.includes('curl') ? t('admin.security.compliance.labels.apiCall') :
                                           event.context_data.user_agent.includes('Mozilla') ? t('admin.security.compliance.labels.browser') :
                                           event.context_data.user_agent}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{event.context_data.user_agent}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                  {event.context_data?.details && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                          {event.context_data.details.format ? t('admin.security.compliance.labels.formatValue', { format: event.context_data.details.format }) :
                                           event.context_data.details.data_types ? t('admin.security.compliance.labels.dataValue', { data: event.context_data.details.data_types.join(', ') }) :
                                           t('admin.security.compliance.labels.additionalDetails')}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{JSON.stringify(event.context_data.details, null, 2)}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                  {event.error_message && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="red.500" lineClamp={1}>
                                          {t('admin.security.compliance.labels.errorValue', { error: event.error_message })}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{event.error_message}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                </VStack>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    ) : (
                      <VStack gap={3} py={6}>
                        <Icon as={FiEye} color="gray.400" boxSize={8} />
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          {t('admin.security.compliance.empty.noRecentAccessEvents')}
                        </Text>
                        <Text fontSize="xs" color="gray.400" textAlign="center">
                          {t('admin.security.compliance.empty.accessEventsHint')}
                        </Text>
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.CloseTrigger asChild>
                  <Button variant="ghost">{t('admin.security.compliance.buttons.close')}</Button>
                </Dialog.CloseTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>

        {/* Audit Logs Modal */}
        <Dialog.Root open={isAuditLogsOpen} onOpenChange={(e) => setIsAuditLogsOpen(e.open)} size="full">
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="90vw" maxH="85vh">
              <Dialog.Header>
                <Dialog.Title>{t('admin.security.compliance.sections.auditLogs')}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  {/* Summary Stats */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                    <VStack gap={2} p={3} bg="blue.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.totalMatchingEvents')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {modalAuditLogs?.total || 0}
                      </Text>
                    </VStack>
                    <VStack gap={2} p={3} bg="green.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.successRatePage')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {modalAuditLogs?.logs && modalAuditLogs.logs.length > 0 
                          ? Math.round((modalAuditLogs.logs.filter((log: any) => log.success).length / modalAuditLogs.logs.length) * 100)
                          : 0}%
                      </Text>
                    </VStack>
                    <VStack gap={2} p={3} bg="orange.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="semibold">{t('admin.security.compliance.labels.failedPage')}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">
                        {modalAuditLogs?.logs?.filter((log: any) => !log.success).length || 0}
                      </Text>
                    </VStack>
                  </SimpleGrid>

                  {/* Security Alerts */}
                  {modalAuditLogs?.security_alerts?.suspicious_ips && modalAuditLogs.security_alerts.suspicious_ips.length > 0 && (
                    <Alert.Root status="warning" borderRadius="md">
                      <Alert.Indicator />
                      <VStack align="start" gap={1}>
                        <Text fontWeight="medium">{t('admin.security.compliance.alerts.suspiciousActivityDetected')}</Text>
                        <Text fontSize="sm">
                          {t('admin.security.compliance.alerts.suspiciousIps', { ips: modalAuditLogs.security_alerts.suspicious_ips.join(', ') })}
                        </Text>
                      </VStack>
                    </Alert.Root>
                  )}

                  {/* Filter Toolbar */}
                  <HStack gap={3} flexWrap="wrap" align="end">
                    <Box flex="1" minW="220px">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t('admin.security.compliance.filters.search')}</Text>
                      <Input
                        size="sm"
                        placeholder={t('admin.security.compliance.placeholders.searchAudit')}
                        value={auditSearchInput}
                        onChange={(e) => setAuditSearchInput(e.target.value)}
                      />
                    </Box>
                    <Box minW="160px">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t('admin.security.compliance.table.status')}</Text>
                      <select
                        value={auditStatusFilter}
                        onChange={(e) => setAuditStatusFilter(e.target.value as '' | 'success' | 'failed')}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px' }}
                      >
                        <option value="">{t('admin.security.compliance.filters.allStatuses')}</option>
                        <option value="success">{t('admin.security.compliance.status.success')}</option>
                        <option value="failed">{t('admin.security.compliance.status.failed')}</option>
                      </select>
                    </Box>
                    <Box minW="160px">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t('admin.security.compliance.table.action')}</Text>
                      <Input
                        size="sm"
                        placeholder={t('admin.security.compliance.placeholders.actionExample')}
                        value={auditActionFilter}
                        onChange={(e) => setAuditActionFilter(e.target.value)}
                      />
                    </Box>
                    <Box minW="160px">
                      <Text fontSize="xs" color="gray.500" mb={1}>{t('admin.security.compliance.filters.resourceType')}</Text>
                      <Input
                        size="sm"
                        placeholder={t('admin.security.compliance.placeholders.resourceExample')}
                        value={auditResourceFilter}
                        onChange={(e) => setAuditResourceFilter(e.target.value)}
                      />
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setAuditSearchInput('');
                        setAuditSearch('');
                        setAuditActionFilter('');
                        setAuditResourceFilter('');
                        setAuditStatusFilter('');
                        setAuditPage(0);
                      }}
                    >
                      {t('admin.security.compliance.buttons.clear')}
                    </Button>
                  </HStack>

                  {/* Events Table */}
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">{t('admin.security.compliance.labels.auditEvents')}</Text>
                    {modalAuditLoading && <Spinner size="sm" />}
                  </HStack>
                  <Box maxH="400px" overflowY="auto" overflowX="auto">
                    {modalAuditLogs?.logs && modalAuditLogs.logs.length > 0 ? (
                      <Table.Root variant="line" size="sm" minW="1000px">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader fontSize="xs" w="12%">{t('admin.security.compliance.table.action')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="20%">{t('admin.security.compliance.table.user')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="15%">{t('admin.security.compliance.table.ipAddress')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="8%">{t('admin.security.compliance.table.status')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="12%">{t('admin.security.compliance.table.resource')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="10%">{t('admin.security.compliance.table.time')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="10%">{t('admin.security.compliance.table.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader fontSize="xs" w="13%">{t('admin.security.compliance.table.details')}</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {modalAuditLogs.logs.map((log: any) => (
                            <Table.Row key={log.id} _hover={{ bg: 'gray.50' }}>
                              <Table.Cell>
                                <HStack gap={2}>
                                  <Icon 
                                    as={log.action.includes('login') ? FiUser : 
                                        log.action.includes('logout') ? FiLogOut :
                                        log.action.includes('data') ? FiDatabase :
                                        FiEye} 
                                    color={log.success ? 'green.500' : 'red.500'} 
                                    boxSize={4} 
                                  />
                                  <VStack align="start" gap={0}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {log.action.replace('user_', '').replace('_', ' ').toUpperCase()}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {auditActionLabel(log.action)}
                                    </Text>
                                  </VStack>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                    {log.username || log.context_data?.email || (log.user_id ? t('admin.security.compliance.labels.userWithId', { id: log.user_id }) : t('admin.security.compliance.labels.system'))}
                                  </Text>
                                  {log.context_data?.login_method && (
                                    <Badge size="xs" colorScheme="blue" variant="subtle">
                                      {log.context_data.login_method}
                                    </Badge>
                                  )}
                                </VStack>
                              </Table.Cell>
                              <Table.Cell>
                                <HStack gap={1}>
                                  <Icon as={FiMapPin} color="gray.400" boxSize={3} />
                                  <Text fontSize="sm" fontFamily="mono" color="gray.700">
                                    {log.ip_address || t('admin.security.compliance.status.notAvailable')}
                                  </Text>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <Badge 
                                  colorScheme={log.success ? 'green' : 'red'} 
                                  size="sm"
                                  variant="subtle"
                                >
                                  {log.success ? t('admin.security.compliance.status.success') : t('admin.security.compliance.status.failed')}
                                </Badge>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {log.context_data?.resource_type || log.resource_type || t('admin.security.compliance.resources.authentication')}
                                  </Text>
                                  {log.context_data?.gdpr_action && (
                                    <Badge size="xs" colorScheme="purple" variant="subtle">
                                      {log.context_data.gdpr_action}
                                    </Badge>
                                  )}
                                </VStack>
                              </Table.Cell>
                              <Table.Cell>
                                <HStack gap={1}>
                                  <Icon as={FiClock} color="gray.400" boxSize={3} />
                                  <Text fontSize="sm" fontFamily="mono">
                                    {new Date(log.created_at).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    })}
                                  </Text>
                                </HStack>
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm">
                                  {new Date(log.created_at).toLocaleDateString()}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <VStack align="start" gap={1}>
                                  {log.context_data?.user_agent && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                          {log.context_data.user_agent.includes('curl') ? t('admin.security.compliance.labels.apiCall') :
                                           log.context_data.user_agent.includes('Mozilla') ? t('admin.security.compliance.labels.browser') :
                                           log.context_data.user_agent}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{log.context_data.user_agent}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                  {log.context_data?.details && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                          {log.context_data.details.format ? t('admin.security.compliance.labels.formatValue', { format: log.context_data.details.format }) :
                                           log.context_data.details.data_types ? t('admin.security.compliance.labels.dataValue', { data: log.context_data.details.data_types.join(', ') }) :
                                           t('admin.security.compliance.labels.additionalDetails')}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{JSON.stringify(log.context_data.details, null, 2)}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                  {log.error_message && (
                                    <Tooltip.Root>
                                      <Tooltip.Trigger asChild>
                                        <Text fontSize="xs" color="red.500" lineClamp={1}>
                                          {t('admin.security.compliance.labels.errorValue', { error: log.error_message })}
                                        </Text>
                                      </Tooltip.Trigger>
                                      <Tooltip.Content>{log.error_message}</Tooltip.Content>
                                    </Tooltip.Root>
                                  )}
                                </VStack>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    ) : (
                      <VStack gap={3} py={6}>
                        <Icon as={FiEye} color="gray.400" boxSize={8} />
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          {modalAuditLoading ? t('admin.security.compliance.loadingAuditEvents') : t('admin.security.compliance.empty.noMatchingAuditEvents')}
                        </Text>
                        <Text fontSize="xs" color="gray.400" textAlign="center">
                          {t('admin.security.compliance.empty.adjustSearchFilters')}
                        </Text>
                      </VStack>
                    )}
                  </Box>

                  {/* Pagination Controls */}
                  {(() => {
                    const total = modalAuditLogs?.total || 0;
                    const totalPages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));
                    const rangeStart = total === 0 ? 0 : auditPage * AUDIT_PAGE_SIZE + 1;
                    const rangeEnd = Math.min((auditPage + 1) * AUDIT_PAGE_SIZE, total);
                    return (
                      <HStack justify="space-between" flexWrap="wrap" gap={2}>
                        <Text fontSize="sm" color="gray.600">
                          {t('admin.security.compliance.pagination.showingRange', { start: rangeStart, end: rangeEnd, total })}
                        </Text>
                        <HStack gap={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={auditPage <= 0 || modalAuditLoading}
                            onClick={() => setAuditPage((p) => Math.max(0, p - 1))}
                          >
                            {t('admin.security.compliance.pagination.previous')}
                          </Button>
                          <Text fontSize="sm" color="gray.600">
                            {t('admin.security.compliance.pagination.pageOf', { page: auditPage + 1, totalPages })}
                          </Text>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={auditPage + 1 >= totalPages || modalAuditLoading}
                            onClick={() => setAuditPage((p) => p + 1)}
                          >
                            {t('admin.security.compliance.pagination.next')}
                          </Button>
                        </HStack>
                      </HStack>
                    );
                  })()}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.CloseTrigger asChild>
                  <Button variant="ghost">{t('admin.security.compliance.buttons.close')}</Button>
                </Dialog.CloseTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </VStack>
    </Box>
  );
};

export default SecurityComplianceSection; 